import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { BiSolidPencil } from 'react-icons/bi';
import { FaExternalLinkAlt, FaPlus } from 'react-icons/fa';
import { ImArrowUp } from 'react-icons/im';
import { IoEllipsisVertical } from 'react-icons/io5';
import { MdDelete } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SearchOutlined } from '@ant-design/icons';
import {
  ConfigProvider,
  Dropdown,
  Input,
  MenuProps,
  Table,
  Tooltip,
} from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';

type SortOrder = 'ascend' | 'descend' | null;
import OpterraPhoenixLogo from 'assets/images/OpterraPhoenixLogo.png';
import Button from 'components/common/Button';
import AddNewButton from 'components/common/Button/AddNewButton';
import DeleteOrganisationModal from 'components/DeleteOrganisationModal/DeleteOrganisationModal';
import OrganisationCreateEditModal from 'components/OrganisationCreateEditModal/OrganisationCreateEditModal';
import { FARMS_PAGE_PATH } from 'constants/routeConstants';
import {
  DELETE_ORGANISATION_MENU_ITEM_LABEL,
  ORGANISATIONS_PAGE_TITLE,
} from 'constants/uiConstants';
import useDebounce from 'hooks/common/useDebounce';
import useFetchOrganisations from 'hooks/useFetchOrganisations';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import { OrganisationWithExtras } from 'interfaces/IOrganisations';
import MergeClassNames from 'utils/MergeClassNames';

import { setActiveOrganisationId } from '../../redux/slices/ActiveOrganisationSlice';
import { clearFarmSelection } from '../../redux/slices/FarmSlice';
import { clearInputSelection } from '../../redux/slices/InputSlice';
import { AppDispatch, RootState } from '../../redux/Store';

const OrgContextMenuRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    record?: OrganisationWithExtras;
    getMenuItems?: (record: OrganisationWithExtras) => MenuProps['items'];
  }
>(({ record, getMenuItems, children, ...restProps }, ref) => (
  <Dropdown
    trigger={['contextMenu']}
    menu={{ items: record && getMenuItems ? getMenuItems(record) : [] }}
  >
    <tr ref={ref} {...restProps}>
      {children}
    </tr>
  </Dropdown>
));
OrgContextMenuRow.displayName = 'OrgContextMenuRow';

const OrganisationsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchText = useDebounce(searchText, 300);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedOrg, setSelectedOrg] = useState<
    OrganisationWithExtras | undefined
  >(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [orgPendingDelete, setOrgPendingDelete] = useState<
    OrganisationWithExtras | undefined
  >(undefined);

  const openCreateModal = useCallback(() => {
    setSelectedOrg(undefined);
    setModalMode('create');
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((org: OrganisationWithExtras) => {
    setSelectedOrg(org);
    setModalMode('edit');
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedOrg(undefined);
  }, []);

  const openDeleteModal = useCallback((org: OrganisationWithExtras) => {
    setOrgPendingDelete(org);
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setOrgPendingDelete(undefined);
  }, []);

  const { organisationId: activeOrganisationId } = useSelector(
    (state: RootState) => state.activeOrganisationReducer
  );

  const {
    data: organisations = [],
    isLoading: isLoadingOrgs,
    error,
    refetch: fetchOrganisations,
  } = useFetchOrganisations();

  const { isLoading: isLoadingUserData } = useFetchUserProfile();
  const isLoading = isLoadingOrgs || isLoadingUserData;

  /** Navigate to /farms immediately — let FarmsPage handle loading UX */
  const navigateToOrg = useCallback(
    (orgId: string) => {
      dispatch(setActiveOrganisationId(orgId));
      dispatch(clearFarmSelection());
      dispatch(clearInputSelection());
      navigate(FARMS_PAGE_PATH);
    },
    [dispatch, navigate]
  );

  const getOrgMenuItems = useCallback(
    (record: OrganisationWithExtras): MenuProps['items'] => [
      {
        key: 'open',
        label: 'Open',
        icon: <FaExternalLinkAlt size={16} />,
        onClick: () => navigateToOrg(record.id),
      },
      {
        key: 'edit',
        label: 'Edit details',
        icon: <BiSolidPencil size={16} />,
        onClick: () => openEditModal(record),
      },
      {
        key: 'delete',
        label: DELETE_ORGANISATION_MENU_ITEM_LABEL,
        icon: <MdDelete size={16} />,
        onClick: () => openDeleteModal(record),
      },
    ],
    [navigateToOrg, openEditModal, openDeleteModal]
  );

  const filteredOrganisations = useMemo(() => {
    if (!debouncedSearchText) return organisations;

    const term = debouncedSearchText.toLowerCase();

    return organisations.filter((org: OrganisationWithExtras) =>
      org?._searchText?.includes(term)
    );
  }, [organisations, debouncedSearchText]);

  const baseColumns = useMemo<ColumnsType<OrganisationWithExtras>>(
    () => [
      {
        title: 'Name',
        dataIndex: 'orgName',
        key: 'name',
        sorter: (a, b) => a.orgName.localeCompare(b.orgName),
        defaultSortOrder: 'ascend',
        className: 'font-semibold',
        width: '35%',
      },
      {
        title: 'Address',
        dataIndex: 'formattedAddress',
        key: 'address',
        sorter: (a, b) => a.formattedAddress.localeCompare(b.formattedAddress),
        width: '20%',
      },
      {
        title: 'Contact',
        dataIndex: 'contactName',
        key: 'contact',
        sorter: (a, b) => a.contactName.localeCompare(b.contactName),
      },
    ],
    []
  );

  const hasData = filteredOrganisations?.length > 0;

  const columns = useMemo(() => {
    const cols = [...baseColumns];

    const nameColIdx = cols.findIndex((c) => c.key === 'name');
    if (nameColIdx !== -1) {
      const nameCol = cols[nameColIdx];
      cols[nameColIdx] = {
        ...nameCol,
        render: (text: string, record: OrganisationWithExtras) => (
          <div className="leading-loose">
            <Tooltip title={text}>
              <div className="truncate">{text}</div>
            </Tooltip>
            <div className="mb-[-1.25rem] select-none whitespace-nowrap text-xs font-normal text-gray-dark opacity-0 transition group-hover:opacity-100">
              {activeOrganisationId === record.id
                ? 'Currently open'
                : 'Double-click to open'}
            </div>
          </div>
        ),
      };
    }

    cols.push({
      title:
        filteredOrganisations?.length > 0 ? (
          <div className="-mr-0 flex justify-end">
            <Button
              onClick={openCreateModal}
              className="flex items-center gap-1 px-2 py-1"
              variant="success"
              disabled={isLoading}
            >
              <FaPlus className="text-white" size={10} />
              <span className="text-sm">Create Organisation</span>
            </Button>
          </div>
        ) : null,
      key: 'options',
      align: 'right',
      render: (_, record) => (
        <div className="ml-auto flex w-fit items-center justify-end gap-6 pr-2 opacity-0 transition group-hover:opacity-100">
          <Tooltip title="More options">
            <Dropdown
              placement="bottomRight"
              align={{ offset: [-20, -20] }}
              autoAdjustOverflow={false}
              trigger={['click']}
              menu={{ items: getOrgMenuItems(record) }}
            >
              <span className="w-fit cursor-pointer rounded-md py-[var(--spacing-thin)] text-xl text-gray-muted hover:bg-gray-argent hover:text-gray-dark">
                <IoEllipsisVertical />
              </span>
            </Dropdown>
          </Tooltip>
        </div>
      ),
    });

    return cols.map((col) =>
      col.sorter
        ? {
            ...col,
            sortIcon: ({ sortOrder }: { sortOrder: SortOrder }) => (
              <ImArrowUp
                className={MergeClassNames(
                  'h-3 w-3 text-gray-muted transition',
                  sortOrder === 'ascend' ? '' : 'rotate-180',
                  sortOrder ? 'opacity-100' : 'opacity-30'
                )}
              />
            ),
          }
        : col
    );
  }, [
    hasData,
    baseColumns,
    isLoading,
    activeOrganisationId,
    getOrgMenuItems,
    openCreateModal,
  ]);

  return (
    <div className="relative px-8 py-4">
      <OrganisationCreateEditModal
        mode={modalMode}
        open={modalOpen}
        onClose={closeModal}
        organisation={selectedOrg}
      />

      <DeleteOrganisationModal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        organisation={orgPendingDelete}
      />

      <Helmet title={ORGANISATIONS_PAGE_TITLE} />
      <div className="mb-4">
        <h2 className="mb-4 text-2xl text-gray-dark">Organisations</h2>

        {organisations?.length === 0 && (
          <Button
            className="flex items-center gap-1"
            onClick={openCreateModal}
            disabled={isLoading}
          >
            <img
              src={OpterraPhoenixLogo}
              alt="Logo"
              className="h-4 w-auto brightness-0 invert"
            />
            <span className="text-sm">
              Create an Organisation to get started
            </span>
          </Button>
        )}

        {organisations.length > 0 && (
          <Input
            placeholder="Search by name, address, or contact"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchOutlined className="text-gray-muted" />}
            className="mb-4 max-w-md"
            disabled={isLoading}
            allowClear
          />
        )}
      </div>

      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerSplitColor: 'transparent',
              cellPaddingInline: 6,
            },
          },
        }}
      >
        <Table
          dataSource={filteredOrganisations}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={false}
          className="organisations-table w-full border-b-2 border-b-secondary"
          scroll={{ y: '70vh' }}
          rowClassName={(record) =>
            MergeClassNames(
              'group cursor-pointer transition',
              activeOrganisationId &&
                record.id === activeOrganisationId &&
                'org-row-highlighted'
            )
          }
          components={{ body: { row: OrgContextMenuRow } }}
          onRow={(record) => ({
            record,
            getMenuItems: getOrgMenuItems,
            onDoubleClick: () => navigateToOrg(record.id),
          })}
          locale={{
            emptyText: error ? (
              <OrganisationErrorState onRetry={() => fetchOrganisations()} />
            ) : searchText ? (
              <EmptySearchState onClear={() => setSearchText('')} />
            ) : (
              <EmptyTableState onOpen={openCreateModal} />
            ),
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default OrganisationsPage;

const OrganisationStateContainer = ({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'danger' | 'none';
}) => {
  const toneClasses =
    tone === 'danger'
      ? 'bg-danger-light text-danger'
      : tone === 'neutral'
        ? 'bg-white text-gray-dark'
        : 'bg-transparent text-transparent shadow-none';

  return (
    <div className="flex h-[65vh] w-full items-center justify-center bg-gray-smoke p-4">
      <div
        className={MergeClassNames(
          'flex w-full max-w-md flex-col items-center gap-4 rounded-md p-6 text-center shadow-sm',
          toneClasses
        )}
      >
        {children}
      </div>
    </div>
  );
};

const OrganisationErrorState = ({ onRetry }: { onRetry: () => void }) => (
  <OrganisationStateContainer tone="danger">
    <p className="text-lg font-medium">
      Failed to load organisations. Please try again.
    </p>

    <Button
      variant="danger"
      className="px-4 py-1 text-sm font-medium"
      onClick={onRetry}
    >
      Retry
    </Button>
  </OrganisationStateContainer>
);

const EmptySearchState = ({ onClear }: { onClear: () => void }) => (
  <OrganisationStateContainer>
    <p className="text-lg font-medium">No organisations match your search.</p>

    <Button
      onClick={onClear}
      className="px-4 py-1 text-sm font-medium text-gray-muted hover:bg-gray-smoke hover:text-gray-dark"
      variant="secondary"
    >
      Clear Search
    </Button>
  </OrganisationStateContainer>
);

const EmptyTableState = ({ onOpen }: { onOpen: () => void }) => (
  <OrganisationStateContainer tone="none">
    <AddNewButton
      onClick={onOpen}
      className="flex items-center rounded-md bg-white px-28 py-5"
      text="Create Organisation"
    />
  </OrganisationStateContainer>
);
